import React, { useState } from "react";
import { Menu } from "lucide-react";
import Sidebar from "./Sidebar";
import CoreQuestionCard from "./CoreQuestionCard";
import SectionNavCard from "./SectionNavCard";
import IntroCard from "./IntroCard";
import AISupplementCard from "./AISupplementCard";
import PracticeView from "./PracticeView";
import AdaptiveView from "./AdaptiveView";
import KnowledgeMapView from "./KnowledgeMapView";
import ListeningModuleView from "./ListeningModuleView";
import ListeningReadingModuleView2 from "./ListeningReadingModuleView2";
import { sectionData } from "../data/chapters";
import { questionBank } from "../data/question_bank";
import { readingPractice } from "../data/reading_practice";

const ALL_QUESTIONS_MAP = {};
[...questionBank.reading, ...questionBank.listening, ...questionBank.listeningReading]
  .forEach(q => { ALL_QUESTIONS_MAP[q.id] = q; });

export default function Layout() {
  const [activeChapterId, setActiveChapterId] = useState(7);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [isAdaptive, setIsAdaptive] = useState(false);
  const [isMap, setIsMap] = useState(false);
  const [mapPracticeNode, setMapPracticeNode] = useState(null);
  const [mapViewKey, setMapViewKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const data = sectionData[activeChapterId];

  const effectiveSectionId =
    activeSectionId ||
    data?.defaultSectionId ||
    (data && data.sections.length > 0 ? data.sections[0].id : null);

  const activeSection =
    data && effectiveSectionId
      ? data.sections.find((s) => s.id === effectiveSectionId)
      : null;

  function handleChapterSelect(id) {
    setActiveChapterId(id);
    setActiveSectionId(null);
    setIsAdaptive(false);
    setIsMap(false);
    setMapPracticeNode(null);
    setSidebarOpen(false);
  }

  function handleMapNodeClick(node) {
    const questions = node.questionIds.map(id => ALL_QUESTIONS_MAP[id]).filter(Boolean);
    setMapPracticeNode({ label: node.label, questions });
  }

  function handleSectionSelect(id) {
    setActiveSectionId(id);
  }

  return (
    <div className="flex h-screen bg-white overflow-hidden">

      {/* ── 移动端侧边栏遮罩 ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── 侧边栏 ── */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <Sidebar
          activeChapterId={activeChapterId}
          onChapterSelect={handleChapterSelect}
          isAdaptive={isAdaptive}
          onAdaptiveSelect={() => {
            setIsAdaptive(true); setIsMap(false);
            setSidebarOpen(false);
          }}
          isMap={isMap}
          onMapSelect={() => {
            setIsMap(true); setIsAdaptive(false);
            setMapPracticeNode(null); setMapViewKey(k => k + 1);
            setSidebarOpen(false);
          }}
        />
      </div>

      {/* ── 主内容区 ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* 移动端顶栏 */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 shrink-0 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
          >
            <Menu size={20} className="text-gray-600" />
          </button>
          <span className="text-sm font-semibold text-gray-800">BJT刷题</span>
        </div>

        {/* 知识点地图 → 练习 面包屑 */}
        {isMap && mapPracticeNode && (
          <header className="flex items-center gap-2 px-4 lg:px-8 pt-4 pb-4 border-b border-gray-100 shrink-0">
            <button
              onClick={() => { setMapPracticeNode(null); setMapViewKey(k => k + 1); }}
              className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              ← 知识点地图
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-sm font-medium text-gray-700">{mapPracticeNode.label}</span>
          </header>
        )}

        {/* 章节标题 */}
        {!isAdaptive && !isMap && data && (
          <header className="flex items-start justify-between px-4 lg:px-8 pt-4 lg:pt-5 pb-4 border-b border-gray-100 shrink-0">
            <div>
              <h2 className="text-base lg:text-lg font-semibold text-gray-800">
                {data.chapterTitle}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">内容由AI生成</p>
            </div>
            <span className="text-xs font-semibold text-indigo-500 border border-indigo-200 rounded-full px-2.5 py-0.5 mt-1">
              Beta
            </span>
          </header>
        )}

        {/* ── 主体 ── */}
        <main className="flex-1 overflow-y-auto px-3 sm:px-4 lg:px-8 py-4 lg:py-6">

          {isMap ? (
            mapPracticeNode ? (
              <PracticeView questionSet={mapPracticeNode.questions} />
            ) : (
              <KnowledgeMapView key={mapViewKey} onNodeClick={handleMapNodeClick} />
            )
          ) : isAdaptive ? (
            <AdaptiveView />
          ) : data ? (
            <div className="w-full lg:max-w-4xl lg:mx-auto">
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">

                {/* 左侧导航 */}
                <div className="lg:w-56 lg:shrink-0 lg:sticky lg:top-6 lg:self-start">
                  <CoreQuestionCard question={data.coreQuestion} />
                  {data.sections.length > 0 && (
                    <SectionNavCard
                      sections={data.sections}
                      activeSectionId={effectiveSectionId}
                      onSectionSelect={handleSectionSelect}
                    />
                  )}
                </div>

                {/* 章节内容 */}
                <div className="flex-1 min-w-0">
                  {activeSection?.type === "listeningReading2" ? (
                    <ListeningReadingModuleView2 questions={activeSection.subsections} />
                  ) : activeSection?.type === "listening" ? (
                    <ListeningModuleView />
                  ) : activeSection?.readingSource != null ? (
                    <PracticeView
                      questionSet={readingPractice[activeSection.readingSource] || []}
                    />
                  ) : activeSection?.readingTypes != null ? (
                    <PracticeView
                      questionSet={questionBank.reading.filter(
                        (q) => activeSection.readingTypes.includes(q.type)
                      )}
                    />
                  ) : (
                    activeSection && (
                      <div className="flex-1 min-w-0">
                        <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-5 leading-snug">
                          {activeSection.title}
                        </h1>

                        <IntroCard intro={activeSection.intro} />

                        {activeSection.subsections.map((sub) => (
                          <div key={sub.id} className="mb-8">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-1 h-6 bg-indigo-500 rounded-full" />
                              <h2 className="text-lg lg:text-xl font-bold text-gray-900">
                                {sub.title}
                              </h2>
                            </div>

                            <p className="text-sm text-gray-700 leading-relaxed mb-1">
                              {sub.body}
                              <span className="text-indigo-600 font-semibold">
                                {" "}
                                {sub.highlights}
                              </span>
                            </p>

                            {sub.aiSupplement && (
                              <AISupplementCard points={sub.aiSupplement.points} />
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>

              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400 text-sm">该章节内容即将上线</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
